import type { HistoryEntry } from '../state/historyStore';

interface StoredHistoryEntry extends HistoryEntry {
  storageBytes: number;
}

const DB_NAME = 'photo-studio-history';
const STORE_NAME = 'entries';
const DB_VERSION = 2;
const MAX_DOM_ENTRIES = 50;

const supportsIndexedDB = typeof indexedDB !== 'undefined';

let dbPromise: Promise<IDBDatabase> | null = null;
const inMemoryStore = new Map<string, StoredHistoryEntry>();

function openDatabase(): Promise<IDBDatabase> {
  if (!supportsIndexedDB) {
    return Promise.reject(new Error('IndexedDB is not available in this environment.'));
  }
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
      const oldVersion = event.oldVersion ?? 0;
      if (oldVersion < 2) {
        const transaction = request.transaction;
        if (transaction) {
          const store = transaction.objectStore(STORE_NAME);
          const cursorRequest = store.openCursor();
          cursorRequest.onsuccess = () => {
            const cursor = cursorRequest.result;
            if (!cursor) {
              return;
            }
            const value = cursor.value as StoredHistoryEntry;
            if (!value.result.originalDataUrl) {
              value.result = {
                ...value.result,
                originalDataUrl: value.result.dataUrl
              };
              cursor.update(value);
            }
            cursor.continue();
          };
        }
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open IndexedDB'));
    };
  });

  return dbPromise;
}

export async function loadAllEntries(): Promise<StoredHistoryEntry[]> {
  if (!supportsIndexedDB) {
    return Array.from(inMemoryStore.values()).sort((a, b) =>
      a.createdAt < b.createdAt ? 1 : -1
    );
  }

  const db = await openDatabase();
  return new Promise<StoredHistoryEntry[]>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      const entries = (request.result ?? []) as StoredHistoryEntry[];
      resolve(entries.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)));
    };
    request.onerror = () => {
      reject(request.error ?? new Error('Failed to read entries from IndexedDB'));
    };
  });
}

export async function saveEntry(entry: StoredHistoryEntry): Promise<void> {
  if (!supportsIndexedDB) {
    inMemoryStore.set(entry.id, entry);
    const allEntries = Array.from(inMemoryStore.values()).sort((a, b) =>
      a.createdAt < b.createdAt ? 1 : -1
    );
    allEntries.slice(MAX_DOM_ENTRIES).forEach((removed) => inMemoryStore.delete(removed.id));
    return;
  }

  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(entry);
    request.onsuccess = () => resolve();
    request.onerror = () => {
      reject(request.error ?? transaction.error ?? new Error('Failed to save entry'));
    };
  });
  const entries = await loadAllEntries();
  if (entries.length > MAX_DOM_ENTRIES) {
    const toRemove = entries.slice(MAX_DOM_ENTRIES);
    await deleteEntries(toRemove.map((item) => item.id));
  }
}

export async function deleteEntry(id: string): Promise<void> {
  if (!supportsIndexedDB) {
    inMemoryStore.delete(id);
    return;
  }

  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => {
      reject(request.error ?? transaction.error ?? new Error('Failed to delete entry'));
    };
  });
}

export async function deleteEntries(ids: string[]): Promise<void> {
  if (!ids.length) {
    return;
  }
  if (!supportsIndexedDB) {
    ids.forEach((id) => inMemoryStore.delete(id));
    return;
  }

  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    ids.forEach((id) => {
      store.delete(id);
    });
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Failed to delete entries'));
  });
}

export async function clearAllEntries(): Promise<void> {
  if (!supportsIndexedDB) {
    inMemoryStore.clear();
    return;
  }

  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? transaction.error ?? new Error('Failed to clear history'));
  });
}

export function trimEntries(entries: StoredHistoryEntry[]): {
  entries: StoredHistoryEntry[];
  removed: StoredHistoryEntry[];
} {
  if (entries.length <= MAX_DOM_ENTRIES) {
    return { entries, removed: [] };
  }

  const trimmed = entries.slice(0, MAX_DOM_ENTRIES);
  const removed = entries.slice(MAX_DOM_ENTRIES);
  return { entries: trimmed, removed };
}

export function estimateTotalBytes(entries: StoredHistoryEntry[]): number {
  return entries.reduce((sum, entry) => sum + (entry.storageBytes ?? 0), 0);
}

export function isQuotaError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  if (error instanceof DOMException) {
    return error.name === 'QuotaExceededError' || error.code === DOMException.QUOTA_EXCEEDED_ERR;
  }
  if (error instanceof Error && typeof error.message === 'string') {
    return /quota/i.test(error.message);
  }
  return false;
}
